/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.entretiens.ts
 *
 * Messages des ENTRETIENS du module CV Matching (cahier des charges
 * app/docs/CDC-CV-MATCHING.md, vague 3, paragraphes 7 et 8) : la modale
 * « Proposer un entretien » et l onglet Entretiens de l espace exposant, la
 * page « Mes entretiens » du candidat, l écran Entretiens de l organisation.
 * Préfixe `entretiens.`, fusionné dans fr.ts comme fr.cvm.ts.
 *
 * Aucune identité d événement, aucun mot du lexique des activités en dur.
 * Les délais (heures de réponse, minutes de rappel) viennent du serveur et
 * sont injectés en valeurs, jamais écrits ici.
 *
 * Mikael - chef de projet - 2026-09-20
 */
export const messagesEntretiensFr: Record<string, string> = {
  // ── Statuts, partagés par les trois écrans ────────────────────────────
  'entretiens.statut.propose': 'En attente du candidat',
  'entretiens.statut.accepte': 'Confirmé',
  'entretiens.statut.refuse': 'Refusé',
  'entretiens.statut.expire': 'Expiré',
  'entretiens.statut.contre_propose': 'Autre créneau proposé',
  'entretiens.statut.annule': 'Annulé',
  'entretiens.statut.termine': 'Terminé',
  'entretiens.statut.absent': 'Absent',
  'entretiens.statut.inconnu': 'Statut inconnu',

  // 2026-10-06 : l'heure limite, plus une durée déguisée en heure
  // (« avant 23 h 59 min »). Mikael - chef de projet - 2026-10-06
  'entretiens.compte.avant': 'Réponse attendue avant {heure}',
  'entretiens.compte.avantDemain': 'Réponse attendue avant demain {heure}',
  'entretiens.compte.avantLe': 'Réponse attendue avant le {date} à {heure}',
  'entretiens.compte.expire': 'Délai dépassé, libération en cours',
  'entretiens.salle.aucune': 'Salle à confirmer par l’organisation',
  'entretiens.salle.libelle': 'Salle {nom}',
  'entretiens.creneau': '{date}, {debut} à {fin}',
  'entretiens.message.entreprise': 'Message de l’entreprise',
  'entretiens.message.candidat': 'Message du candidat',
  'entretiens.chargement': 'Chargement des entretiens…',
  'entretiens.erreur': 'Les entretiens n’ont pas pu être chargés. Réessayez.',

  // ── Modale « Proposer un entretien » (exposant) ───────────────────────
  'entretiens.proposer.titre': 'Proposer un entretien',
  'entretiens.proposer.a': 'À {nom}',
  'entretiens.proposer.recruteur': 'Recruteur',
  'entretiens.proposer.recruteur.moi': ' (moi)',
  'entretiens.proposer.creneau': 'Créneau',
  'entretiens.proposer.creneau.aucun':
    'Ce recruteur n’a plus de créneau libre. Choisissez un autre recruteur.',
  'entretiens.proposer.creneau.chargement': 'Recherche des créneaux libres…',
  'entretiens.proposer.message': 'Message au candidat (facultatif)',
  'entretiens.proposer.message.placeholder': 'Ce que vous aimeriez aborder, ce qu’il faut apporter…',
  'entretiens.proposer.delai':
    'Le candidat a {heures} h pour accepter, refuser ou proposer un autre créneau. Sans réponse, le créneau se libère.',
  'entretiens.proposer.plafond': '{comptes} entretien(s) sur {plafond} pour votre entreprise.',
  'entretiens.proposer.envoyer': 'Envoyer la proposition',
  'entretiens.proposer.enCours': 'Envoi…',
  'entretiens.proposer.annuler': 'Annuler',
  'entretiens.proposer.fermer': 'Fermer',
  'entretiens.proposer.ok': 'Proposition envoyée : le candidat est prévenu par courriel.',
  'entretiens.proposer.erreur': 'La proposition n’a pas pu être envoyée. Réessayez.',
  'entretiens.proposer.erreur.slot_not_free': 'Ce créneau vient d’être pris. Choisissez-en un autre.',
  'entretiens.proposer.erreur.slot_not_mine': 'Ce créneau n’est pas le vôtre.',
  'entretiens.proposer.erreur.minor_protected':
    'Candidat mineur sans accord parental enregistré : aucun entretien ne peut lui être proposé.',
  'entretiens.proposer.erreur.cv_not_open':
    'Le CV de cette personne n’est pas ouvert à votre entreprise et vous ne l’avez pas rencontrée.',
  'entretiens.proposer.erreur.interview_exists': 'Un entretien est déjà en cours avec ce candidat.',
  'entretiens.proposer.erreur.interview_cap_reached':
    'Le plafond d’entretiens de votre entreprise est atteint ({comptes} sur {plafond}).',
  'entretiens.proposer.erreur.not_premium': 'Votre entreprise n’est pas premium.',
  'entretiens.proposer.erreur.module_indisponible': 'Le module premium n’est pas disponible sur cette instance.',
  'entretiens.proposer.erreur.recruiter_inactive': 'Votre accès recruteur est désactivé.',

  // ── Onglet Entretiens (exposant) ──────────────────────────────────────
  'entretiens.onglet.plafond': '{comptes} sur {plafond} entretiens possibles pour votre entreprise.',
  'entretiens.onglet.vide.titre': 'Aucun entretien pour l’instant',
  'entretiens.onglet.vide.texte':
    'Proposez un entretien depuis l’onglet Trouver, sur la carte d’un candidat. Vous suivrez ici les réponses, les contre-propositions et l’agenda.',
  'entretiens.onglet.attente.titre': 'Propositions en attente',
  'entretiens.onglet.contre.titre': 'Contre-propositions à traiter',
  'entretiens.onglet.contre.texte': '{nom} ne peut pas venir à {ancien} et propose {nouveau}.',
  'entretiens.onglet.contre.accepter': 'Accepter ce créneau',
  'entretiens.onglet.contre.refuser': 'Refuser',
  'entretiens.onglet.confirmes.titre': 'Entretiens confirmés',
  'entretiens.onglet.confirmes.termine': 'Terminé',
  'entretiens.onglet.confirmes.absent': 'Absent',
  'entretiens.onglet.confirmes.annuler': 'Annuler l’entretien',
  'entretiens.onglet.confirmes.annuler.confirmer': 'Annuler l’entretien avec {nom} ? Le candidat sera prévenu et le créneau libéré.',
  'entretiens.onglet.attente.annuler': 'Retirer la proposition',
  'entretiens.onglet.evaluer.titre': 'À évaluer',
  'entretiens.onglet.evaluer.bouton': 'Évaluer',
  'entretiens.onglet.evaluer.bientot': 'Disponible dans une prochaine version (évaluation).',
  // Vague 4 : les entretiens evalues et la fiche d evaluation.
  'entretiens.onglet.evalues.titre': 'Évalués',
  'entretiens.onglet.evaluer.modifier': 'Modifier l’évaluation',
  'entretiens.onglet.evaluer.note': '{note} sur 5',
  'entretiens.onglet.evaluer.notes': '{count, plural, one {# note} other {# notes}}',
  'entretiens.onglet.agenda.titre': 'Agenda par recruteur',
  'entretiens.onglet.agenda.vide': 'Aucun entretien confirmé pour {nom}.',
  'entretiens.onglet.historique.titre': 'Historique',
  'entretiens.onglet.par': 'par {recruteur}',
  'entretiens.onglet.action.ok': 'C’est noté.',
  'entretiens.onglet.action.erreur': 'L’action n’a pas pu être enregistrée. Réessayez.',
  'entretiens.onglet.action.expired': 'Le délai de réponse est dépassé : le créneau a été libéré.',
  'entretiens.onglet.recruteurs.mere':
    'Déclarez vos recruteurs, invitez-les, suivez leur planning depuis la page Mes recruteurs.',
  'entretiens.onglet.recruteurs.lien': 'Ouvrir Mes recruteurs',
  'entretiens.onglet.recruteurs.recruteur':
    'Vous êtes recruteur de {entreprise}. Vos créneaux et vos entretiens sont les vôtres ; la déclaration des recruteurs est réservée au compte principal.',

  // ── Fiche d evaluation (exposant, vague 4) ────────────────────────────
  'entretiens.eval.titre': 'Évaluer l’entretien',
  'entretiens.eval.sousTitre': '{nom}, {date} à {heure}',
  'entretiens.eval.chargement': 'Chargement de l’évaluation…',
  'entretiens.eval.erreur': 'L’évaluation n’a pas pu être chargée.',
  'entretiens.eval.fermer': 'Fermer',
  'entretiens.eval.note': 'Note',
  'entretiens.eval.note.aria': '{n} sur 5',
  'entretiens.eval.note.aucune': 'Aucune note posée',
  'entretiens.eval.decision': 'Décision',
  'entretiens.eval.decision.a_revoir': 'À revoir',
  'entretiens.eval.decision.proposition': 'Proposition à faire',
  'entretiens.eval.decision.ne_pas_retenir': 'Ne pas retenir',
  'entretiens.eval.decision.absent': 'Absent',
  'entretiens.eval.etiquettes': 'Étiquettes',
  'entretiens.eval.etiquettes.aide': 'Un mot, Entrée : l’étiquette est créée à la volée.',
  'entretiens.eval.etiquettes.placeholder': 'Ajouter une étiquette',
  'entretiens.eval.etiquettes.retirer': 'Retirer {etiquette}',
  'entretiens.eval.enregistrer': 'Enregistrer l’évaluation',
  'entretiens.eval.enregistre': 'Évaluation enregistrée.',
  'entretiens.eval.erreurEnregistrement': 'L’évaluation n’a pas pu être enregistrée.',
  'entretiens.eval.noteRequise': 'Choisissez une note de 1 à 5.',
  'entretiens.eval.derniere': 'Dernière modification le {date}.',
  'entretiens.eval.notes.titre': 'Notes horodatées',
  'entretiens.eval.notes.regle': 'Une note ne se modifie pas : on en ajoute une autre.',
  'entretiens.eval.notes.vide': 'Aucune note pour l’instant.',
  'entretiens.eval.notes.placeholder': 'Ce que vous retenez, maintenant…',
  'entretiens.eval.notes.ajouter': 'Ajouter la note',
  'entretiens.eval.notes.ajoutee': 'Note ajoutée.',
  'entretiens.eval.notes.erreur': 'La note n’a pas pu être ajoutée.',
  'entretiens.eval.notes.par': '{heure}, {auteur}',
  'entretiens.eval.resume.bouton': 'Résumer avec l’assistance',
  'entretiens.eval.resume.enCours': 'Résumé en cours…',
  'entretiens.eval.resume.titre': 'Résumé des notes',
  'entretiens.eval.resume.indisponible': 'Résumé non disponible : la fonction d’assistance est désactivée sur cette instance.',
  'entretiens.eval.resume.repli': 'Résumé non disponible ({raison}).',
  'entretiens.eval.resume.notesVides': 'Ajoutez d’abord une note à résumer.',
  'entretiens.eval.interne': 'Ces informations restent internes à votre entreprise : le candidat ne les voit jamais.',

  // ── Grille des creneaux (bloquer, liberer ; vague 4) ─────────────────
  'entretiens.creneaux.titre': 'Créneaux d’entretien',
  'entretiens.creneaux.texte': 'Bloquez un créneau où vous n’êtes pas disponible, libérez-le pour le rouvrir. Un créneau proposé ou confirmé ne se touche pas ici.',
  'entretiens.creneaux.chargement': 'Chargement des créneaux…',
  'entretiens.creneaux.erreur': 'Les créneaux n’ont pas pu être chargés.',
  'entretiens.creneaux.vide': 'Aucun créneau : ils apparaissent quand l’entreprise est premium et qu’un recruteur est déclaré.',
  'entretiens.creneaux.indisponible': 'Le module premium n’est pas disponible sur cette instance.',
  'entretiens.creneaux.nonPremium': 'Votre entreprise n’est pas premium : aucun créneau d’entretien.',
  'entretiens.creneaux.statut.libre': 'Libre',
  'entretiens.creneaux.statut.propose': 'Proposé',
  'entretiens.creneaux.statut.confirme': 'Confirmé',
  'entretiens.creneaux.statut.bloque': 'Bloqué',
  'entretiens.creneaux.bloquer': 'Bloquer',
  'entretiens.creneaux.liberer': 'Libérer',
  'entretiens.creneaux.ok': 'Créneau mis à jour.',
  'entretiens.creneaux.erreurAction': 'Le créneau n’a pas pu être modifié.',
  'entretiens.creneaux.creneau': '{debut} à {fin}',

  // ── Page Mes entretiens (candidat) ────────────────────────────────────
  'entretiens.mes.titre': 'Mes entretiens',
  'entretiens.mes.sousTitre':
    'Les entreprises qui vous proposent un entretien, vos réponses, et votre agenda le jour de l’événement.',
  'entretiens.mes.retour': 'Mon espace',
  'entretiens.mes.vide.titre': 'Aucun entretien pour l’instant',
  'entretiens.mes.vide.texte':
    'Quand une entreprise vous proposera un entretien, vous le verrez ici et vous recevrez un courriel. Vous pourrez accepter, refuser ou proposer un autre créneau.',
  'entretiens.mes.propositions.titre': 'Propositions à traiter',
  'entretiens.mes.avec': '{entreprise}, avec {recruteur}',
  'entretiens.mes.avecFonction': '{entreprise}, avec {recruteur} ({fonction})',
  'entretiens.mes.accepter': 'Accepter',
  'entretiens.mes.refuser': 'Refuser',
  'entretiens.mes.contre': 'Proposer un autre créneau',
  'entretiens.mes.contre.titre': 'Choisissez un autre créneau',
  'entretiens.mes.contre.aide': 'Les créneaux encore libres de {recruteur}.',
  'entretiens.mes.contre.aucun': 'Ce recruteur n’a plus d’autre créneau libre : acceptez ou refusez celui-ci.',
  'entretiens.mes.contre.envoyer': 'Envoyer ma proposition',
  'entretiens.mes.contre.annuler': 'Revenir',
  'entretiens.mes.message': 'Un mot pour l’entreprise (facultatif)',
  'entretiens.mes.refuser.confirmer': 'Refuser l’entretien avec {entreprise} ? Le créneau sera libéré.',
  'entretiens.mes.enAttenteEntreprise.titre': 'En attente de l’entreprise',
  'entretiens.mes.enAttenteEntreprise.texte': 'Vous avez proposé {nouveau}. L’entreprise a jusqu’au {limite} pour répondre.',
  'entretiens.mes.agenda.titre': 'Mon agenda',
  'entretiens.mes.agenda.rappel': 'Vous recevrez un rappel {minutes} minutes avant chaque entretien.',
  'entretiens.mes.historique.titre': 'Historique',
  'entretiens.mes.ok.accepte': 'Entretien confirmé. Vous recevez un courriel de confirmation.',
  'entretiens.mes.ok.refuse': 'Entretien refusé. L’entreprise est prévenue.',
  'entretiens.mes.ok.contre': 'Votre proposition est envoyée. L’entreprise doit maintenant répondre.',
  'entretiens.mes.erreur': 'Votre réponse n’a pas pu être enregistrée. Réessayez.',
  'entretiens.mes.erreur.interview_expired': 'Le délai de réponse est dépassé : le créneau a été libéré.',
  'entretiens.mes.erreur.counter_slot_not_free': 'Ce créneau vient d’être pris. Choisissez-en un autre.',
  'entretiens.mes.rappel.titre': '{count, plural, one {Une entreprise vous propose un entretien} other {# entreprises vous proposent un entretien}}',
  'entretiens.mes.rappel.texte': 'Répondez avant la fin du délai : sans réponse, le créneau est libéré.',
  'entretiens.mes.rappel.lien': 'Voir mes entretiens',
  'entretiens.plus.lien': 'Mes entretiens',

  // ── Écran Entretiens (organisation) ───────────────────────────────────
  'entretiens.admin.titre': 'Entretiens',
  // Cle de menu : le test adminNav exige le prefixe adminNav., comme fr.cvm
  // le fait pour adminNav.premiumSettings.
  'adminNav.interviews': 'Entretiens (premium)',
  'entretiens.admin.sousTitre':
    'Tous les entretiens proposés par les entreprises premium, l’occupation des salles et leur réattribution.',
  'entretiens.admin.indisponible.titre': 'Module premium indisponible',
  'entretiens.admin.indisponible.type': 'Le module premium n’existe que sur un salon de recrutement.',
  'entretiens.admin.indisponible.desactive':
    'Le module premium est désactivé sur cette instance. Activez-le dans Premium : réglages et salles.',
  'entretiens.admin.filtre.statut': 'Statut',
  'entretiens.admin.filtre.tous': 'Tous',
  'entretiens.admin.filtre.salle': 'Salle',
  'entretiens.admin.filtre.sansSalle': 'Sans salle',
  'entretiens.admin.filtre.entreprise': 'Entreprise',
  'entretiens.admin.export': 'Exporter (CSV)',
  'entretiens.admin.total': '{count, plural, =0 {Aucun entretien} one {# entretien} other {# entretiens}}',
  'entretiens.admin.vide': 'Aucun entretien ne correspond à ces filtres.',
  'entretiens.admin.col.heure': 'Heure',
  'entretiens.admin.col.entreprise': 'Entreprise',
  'entretiens.admin.col.recruteur': 'Recruteur',
  'entretiens.admin.col.candidat': 'Candidat',
  'entretiens.admin.col.statut': 'Statut',
  'entretiens.admin.col.salle': 'Salle',
  'entretiens.admin.col.contact': 'Coordonnées',
  'entretiens.admin.mineur': 'mineur',
  'entretiens.admin.salle.reattribuer': 'Changer de salle',
  'entretiens.admin.salle.retirer': 'Aucune salle',
  'entretiens.admin.salle.ok': 'Salle mise à jour.',
  'entretiens.admin.salle.erreur': 'La salle n’a pas pu être changée.',
  'entretiens.admin.salle.pleine': 'Cette salle est pleine à cette heure ({charge} sur {capacite}).',
  'entretiens.admin.occupation.titre': 'Occupation des salles',
  'entretiens.admin.occupation.texte': 'Entretiens confirmés par salle et par heure de début, sur la capacité de la salle.',
  'entretiens.admin.occupation.salle': 'Salle',
  'entretiens.admin.occupation.sansSalle': '{count, plural, one {# entretien confirmé sans salle} other {# entretiens confirmés sans salle}} : à attribuer à la main.',
  'entretiens.admin.occupation.inactive': 'inactive',
  'entretiens.admin.occupation.aucuneSalle':
    'Aucune salle déclarée. Déclarez le pool de salles dans Premium : réglages et salles.',
};
